import { useCallback, useEffect, useMemo, useState } from 'react'
import { POINTS_EVENT_TYPE } from '../../domain/domainCodes'
import {
  Award,
  Check,
  Info,
  Medal,
  Pencil,
  Plus,
  SlidersHorizontal,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  Zap,
} from 'lucide-react'
import ActiveMark from '../../components/ActiveMark'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import Card from '../../components/Card'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import FileField from '../../components/FileField'
import FilterBar from '../../components/FilterBar'
import FormRow from '../../components/FormRow'
import InfoHint from '../../components/InfoHint'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import RowActions from '../../components/RowActions'
import SectionNav from '../../components/SectionNav'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import useHashTab from '../../components/useHashTab'
import { FormBody, FormCols, InfoNote, SwitchField, Textarea } from '../../components/Form'
import { useToast } from '../../components/toastContext'
import {
  RISK_LEVEL_SORTABLE,
  BADGE_SORTABLE,
  ACTIVITY_SORTABLE,
  badgeImageUrl,
  getBadgesPage,
  getPointsSettings,
  listActivities,
  listBadges,
  listRiskLevels,
  saveActivity,
  saveBadge,
  savePointsSettings,
  saveRiskLevel,
  setBadgesAwarding,
} from './gamificationService'
import { gamificationSettingsStrings as S } from './strings'
import './gamification.css'
import { serverDetail, serverText } from '../../lib/i18n'
import { MAX_PAGE_SIZE } from '../../lib/dataTable'
import { inRange, wholeNumber } from './wholeNumber'

const TABS = [
  { id: 'settings', label: S.tabs.settings, icon: SlidersHorizontal },
  { id: 'activities', label: S.tabs.activities, icon: Zap },
  { id: 'badges', label: S.tabs.badges, icon: Medal },
  { id: 'riskLevels', label: S.tabs.riskLevels, icon: TriangleAlert },
]

const TAB_IDS = TABS.map((tab) => tab.id)

/** `PointsEventType` 6 — the one activity whose points come from the score, not a number. */
const EXAM_EVENT_TYPE = POINTS_EVENT_TYPE.PASS_TESTS

/**
 * الإعدادات (التلعيب) — the legacy `PointsActivities`, `PointsBadges` and `PointsRiskLevels`.
 *
 * FOUR TABS, where the clone had three. The one that was missing is the settings block the
 * legacy keeps above its activities table — whether gamification runs at all, whether it
 * notifies, and how the leaderboard is presented. Those are `save-settings`, and nothing in
 * the app could reach them.
 *
 * THREE SLICES BEHIND ONE SCREEN, the same shape as the phishing settings page: activities,
 * badges and risk levels are separate vertical slices with separate routes, but one screen.
 * Taking them together is the only way this screen is coherent.
 *
 * NOTHING HERE CAN BE DELETED. None of the three slices has a delete route — not in this API
 * and not in the legacy controller either. The old screen showed a delete button on every row
 * that closed the dialog and toasted success while leaving the row in place. Deactivating is
 * what the API supports, so that is what the screen offers, and the note says why.
 */
/* The order each `/filter` handler applies when asked for none. */
const DEFAULT_SORT = {
  badges: { key: 'points', dir: 'asc' },
  riskLevels: { key: 'min', dir: 'asc' },
}

export default function GamificationSettingsPage() {
  const [tab, setTab] = useHashTab('settings', TAB_IDS)
  const toast = useToast()

  const [settings, setSettings] = useState(null)
  const [usersCountError, setUsersCountError] = useState('')
  const [badgesPage, setBadgesPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [listLoading, setListLoading] = useState(false)
  /* The search is a working position rather than an address — it would rewrite history on
     every keystroke — but it was plain state, so editing a row and coming back returned to
     a page of a list that was no longer being searched. Keyed per tab: each tab searches
     its own list. */
  const [query, setQuery] = useRemembered(`gamification-settings:${tab}:query`, '')
  /* In the address, so Back and a refresh both land on the same page. */
  const [page, setPage] = usePageParam()
  /* Kept the same way, and for the same reason: a tab set to 100 rows came back at 25. */
  const [pageSize, setPageSize] = useRemembered(`gamification-settings:${tab}:pageSize`, 25)
  /* The order, owned here with the page and sent to the server; each tab's default is the
     order its handler applies on its own, so the arrow agrees with the rows at first. */
  const [sort, setSort] = useRemembered(`gamification-settings:${tab}:sort`, DEFAULT_SORT[tab] ?? null)

  const [editing, setEditing] = useState(null)
  const [values, setValues] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([getPointsSettings(), getBadgesPage()])
      .then(([loadedSettings, loadedBadges]) => {
        if (cancelled) return
        setSettings(loadedSettings)
        setBadgesPage(loadedBadges)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.page)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey, toast])

  /* One list per tab, all the same shape. `settings` has no table. */
  const listFor = useMemo(
    () => ({ activities: listActivities, badges: listBadges, riskLevels: listRiskLevels }),
    [],
  )
  /* What each tab's handler can order by; every other column draws no arrow. */
  const sortableFor = { activities: ACTIVITY_SORTABLE, badges: BADGE_SORTABLE, riskLevels: RISK_LEVEL_SORTABLE }

  useEffect(() => {
    const load = listFor[tab]
    if (!load) return undefined
    let cancelled = false
    setListLoading(true)
    load({ page, pageSize, query, sort })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setTotal(response.total)
        setListLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setListLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
    return () => {
      cancelled = true
    }
  }, [tab, listFor, page, pageSize, query, sort, reloadKey, toast])

  /* Switching tab starts a different list; keeping the old page number would ask for page 4
     of a table with two rows. */
  /* The page starts again on the new tab; the SEARCH does not have to be cleared, because
     each tab remembers its own — clearing it here would wipe the text the other tab was
     left with the moment this one is opened. */
  useOnChange(() => {
    setPage(1)
  }, [tab])

  /* A new search is a new list, so it starts at its first page — on a CHANGE only, or the
     page carried in the address would be thrown away on arrival. The table cannot do this
     itself: these lists are SERVER-paged, so the page belongs to this screen. */
  useOnChange(() => setPage(1), [query])

  const open = (row) => {
    const blank = {
      activities: { eventType: settings?.eventTypes?.[0]?.value ?? 0, points: 0, isPositive: true, effectOpposite: false, percentageOnDuplication: '', isActive: true },
      badges: { nameAr: '', nameEn: '', descriptionAr: '', descriptionEn: '', points: 0, image: '', isActive: true },
      riskLevels: { nameAr: '', nameEn: '', min: 0, max: '', isActive: true },
    }[tab]
    setValues(row ? { ...blank, ...row, max: row.max ?? '' } : blank)
    setEditing(row ?? {})
  }

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  const submit = async () => {
    const save = { activities: saveActivity, badges: saveBadge, riskLevels: saveRiskLevel }[tab]
    setSaving(true)
    const result = await save(values)
    setSaving(false)
    if (!result.ok) return toast.error(serverDetail(result.message) || S.fail.save)
    toast.success(serverText(result.message) || S.saved)
    setEditing(null)
    setReloadKey((key) => key + 1)
    return undefined
  }

  const saveSettings = async () => {
    /* Empty shows everyone; otherwise a whole number the board can show — it asks for one page,
       and a page is at most MAX_PAGE_SIZE rows. "0", "-5" and "abc" all used to save. */
    const usersCount = wholeNumber(settings.leaderBoardUsersCount)
    if (usersCount !== null && !inRange(usersCount, 1, MAX_PAGE_SIZE)) {
      setUsersCountError(S.settings.leaderBoardUsersCountError(MAX_PAGE_SIZE))
      return undefined
    }
    setUsersCountError('')
    setSaving(true)
    const result = await savePointsSettings({ ...settings, leaderBoardUsersCount: usersCount ?? '' })
    setSaving(false)
    if (!result.ok) return toast.error(serverDetail(result.message) || S.fail.settings)
    toast.success(serverText(result.message) || S.saved)
    return undefined
  }

  /* Saves on the click, so the click is confirmed — and a refused save puts the tile
     back on what the server still holds rather than leaving a choice that was not kept. */
  const chooseAwarding = async (awarding) => {
    const before = badgesPage?.awarding
    setBadgesPage((previous) => ({ ...previous, awarding }))
    const result = await setBadgesAwarding(awarding)
    if (!result.ok) {
      setBadgesPage((previous) => ({ ...previous, awarding: before }))
      toast.error(serverDetail(result.message) || S.fail.badges)
      return
    }
    toast.success(S.badgeForm.awardingSaved)
  }

  const setSetting = (key) => (value) => setSettings((previous) => ({ ...previous, [key]: value }))

  /* A PNG as a data URL — the endpoint takes the whole `data:image/png;base64,…` string. */
  const readImage = useCallback((file) => {
    if (!file) return
    const reader = new FileReader()
    reader.addEventListener('load', () => setValues((previous) => ({ ...previous, image: String(reader.result) })))
    reader.readAsDataURL(file)
  }, [])

  const statusColumn = useMemo(
    () => ({
      key: 'isActive',
      header: S.activities.status,
      align: 'center',
      value: (row) => (row.isActive ? S.active : S.inactive),
      render: (row) => (
        <ActiveMark active={Boolean(row.isActive)} label={row.isActive ? S.active : S.inactive} />
      ),
    }),
    [],
  )

  const actionsColumn = useMemo(
    () => ({
      key: 'actions',
      header: <Pencil size={15} strokeWidth={1.75} />,
      align: 'center',
      width: '72px',
      sortable: false,
      searchable: false,
      // Edit only: there is no delete route anywhere in these three slices.
      render: (row) => (
        <RowActions actions={[{ id: 'edit', icon: Pencil, label: S.edit, onClick: () => open(row) }]} />
      ),
    }),
    // `open` closes over `tab` and `settings`, both of which change the blank form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, settings],
  )

  const columns = useMemo(() => {
    if (tab === 'activities') {
      return [
        { key: 'eventTypeName', header: S.activities.name, render: (row) => <bdi>{row.eventTypeName}</bdi> },
        {
          key: 'points',
          header: S.activities.points,
          align: 'center',
          /* The exam activity awards the score itself, so a number here would be a lie. */
          render: (row) => (row.eventType === EXAM_EVENT_TYPE ? <span className="gm__nil">—</span> : row.points),
        },
        {
          key: 'percentageOnDuplication',
          header: S.activities.percentage,
          align: 'center',
          render: (row) =>
            row.percentageOnDuplication == null ? (
              <span className="gm__nil">—</span>
            ) : (
              <span dir="ltr">{row.percentageOnDuplication}%</span>
            ),
        },
        {
          key: 'isPositive',
          header: S.activities.type,
          align: 'center',
          value: (row) => (row.isPositive ? S.activityForm.isPositive : S.activities.type),
          render: (row) =>
            row.isPositive ? (
              <ThumbsUp size={16} strokeWidth={1.75} className="gm__good" />
            ) : (
              <ThumbsDown size={16} strokeWidth={1.75} className="gm__bad" />
            ),
        },
        {
          key: 'effectOpposite',
          header: S.activities.opposite,
          align: 'center',
          value: (row) => (row.effectOpposite ? S.active : S.inactive),
          /* A clear green check where the opposite effect applies (2026-10-22), in place of
             a small text tick; nothing to mark where it does not. */
          render: (row) =>
            row.effectOpposite ? (
              <Check className="gm__good" size={20} strokeWidth={2.75} role="img" aria-label={S.active} />
            ) : (
              <span className="gm__nil">—</span>
            ),
        },
        statusColumn,
        actionsColumn,
      ]
    }

    if (tab === 'badges') {
      return [
        {
          key: 'name',
          header: S.badges.name,
          render: (row) => (
            <span className="gm__badge-cell">
              {/* Only an uploaded picture is asked for; most badges have none. */}
              {row.hasImage && (
                <img
                  className="gm__badge-img"
                  src={badgeImageUrl(badgesPage?.directory, row.id)}
                  alt=""
                  loading="lazy"
                />
              )}
              <bdi>{row.name}</bdi>
            </span>
          ),
        },
        { key: 'points', header: S.badges.points, align: 'center' },
        {
          key: 'descriptionAr',
          header: S.badges.description,
          render: (row) => <bdi>{row.descriptionAr || row.descriptionEn}</bdi>,
        },
        statusColumn,
        actionsColumn,
      ]
    }

    return [
      { key: 'name', header: S.riskLevels.name, render: (row) => <bdi>{row.name}</bdi> },
      { key: 'min', header: S.riskLevels.min, align: 'center' },
      {
        key: 'max',
        header: S.riskLevels.max,
        align: 'center',
        /* Null is an OPEN-ENDED top band, not zero — showing 0 would invert the meaning. */
        render: (row) => (row.max == null ? <span className="gm__nil">{S.riskForm.openEnded}</span> : row.max),
      },
      statusColumn,
      actionsColumn,
    ]
  }, [tab, statusColumn, actionsColumn, badgesPage])
  const sortableColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        sortable: column.sortable !== false && (sortableFor[tab] ?? []).includes(column.key),
      })),
    // `sortableFor` is a constant table of constants.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, tab],
  )

  /* The settings tab has no editor, so it has no labels — and the Drawer below renders its
     title whether or not it is open, which is how `formLabels.add` threw on first paint. */
  const formLabels =
    { activities: S.activityForm, badges: S.badgeForm, riskLevels: S.riskForm }[tab] ?? S.activityForm

  if (loading) {
    return (
      <>
        <PageHeader title={S.title} />
        <PageLoader />
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={S.title}
        scope={<SectionNav groups={[{ items: TABS }]} value={tab} onChange={setTab} ariaLabel={S.title} />}
      />

      {tab === 'settings' && settings && (
        <>
          <Card className="gam-settings-card" title={S.settings.tab}>
            {/* What turning it off means, on the switch it is about rather than under
                the card's title. */}
            <SwitchField
              checked={settings.enabled}
              onChange={setSetting('enabled')}
              label={S.settings.enabled}
              info={S.settings.enabledHint}
            />
            <SwitchField
              checked={settings.notifications}
              onChange={setSetting('notifications')}
              label={S.settings.notifications}
              info={S.settings.notificationsHint}
            />
          </Card>

          <Card
            className="gam-settings-card"
            title={S.settings.leaderBoard}
            footer={
              <Button onClick={saveSettings} disabled={saving}>
                {S.save}
              </Button>
            }
          >
            <SwitchField
              checked={settings.showLeaderBoard}
              onChange={setSetting('showLeaderBoard')}
              label={S.settings.showLeaderBoard}
            />
            <SwitchField
              checked={settings.topUsersOnlyWithBadges}
              onChange={setSetting('topUsersOnlyWithBadges')}
              label={S.settings.topUsersOnlyWithBadges}
            />
            <FormRow label={S.settings.leaderBoardNameAr}>
              {(id) => (
                <TextField id={id} value={settings.leaderBoardNameAr} onChange={setSetting('leaderBoardNameAr')} />
              )}
            </FormRow>
            <FormRow label={S.settings.leaderBoardNameEn}>
              {(id) => (
                <TextField id={id} dir="ltr" value={settings.leaderBoardNameEn} onChange={setSetting('leaderBoardNameEn')} />
              )}
            </FormRow>
            <FormRow
              label={S.settings.leaderBoardUsersCount}
              hint={S.settings.leaderBoardUsersCountHint}
              error={usersCountError}
            >
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  inputMode="numeric"
                  value={String(settings.leaderBoardUsersCount ?? '')}
                  placeholder={S.settings.leaderBoardUsersCountEveryone}
                  onChange={(value) => {
                    setUsersCountError('')
                    setSetting('leaderBoardUsersCount')(value)
                  }}
                  invalid={!!usersCountError}
                />
              )}
            </FormRow>
          </Card>
        </>
      )}

      {tab === 'badges' && badgesPage && (
        /* Two choice tiles rather than an underline switch (2026-10-22): each shows what
           a profile will carry — the whole row of badges, or only the newest of them —
           so the choice reads before its label does. Real radios underneath, so the
           arrow keys and a screen reader treat it as the one-of-two it is. */
        <Card
          className="gm-award"
          title={
            <>
              {S.badgeForm.awarding}
              <InfoHint content={<bdi>{S.badgeForm.awardingHint}</bdi>} />
            </>
          }
        >
          <div className="gm-award__options" role="radiogroup" aria-label={S.badgeForm.awarding}>
            {[
              { id: 'all', label: S.badgeForm.awardingAll },
              { id: 'last', label: S.badgeForm.awardingLast },
            ].map((option) => {
              const selected = badgesPage.awarding === option.id
              return (
                <label key={option.id} className={`gm-award__option${selected ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="badges-awarding"
                    className="gm-award__input"
                    value={option.id}
                    checked={selected}
                    onChange={() => chooseAwarding(option.id)}
                  />
                  <span className="gm-award__preview" aria-hidden="true">
                    {[0, 1, 2].map((index) => (
                      <span
                        key={index}
                        className={`gm-award__badge${option.id === 'last' && index < 2 ? ' is-faded' : ''}`}
                      >
                        <Award size={14} strokeWidth={1.75} />
                      </span>
                    ))}
                  </span>
                  <span className="gm-award__radio" aria-hidden="true" />
                  <span className="gm-award__label">
                    <bdi>{option.label}</bdi>
                  </span>
                </label>
              )
            })}
          </div>
        </Card>
      )}

      {tab !== 'settings' && (
        <>
          <FilterBar
            search={{ value: query, onChange: setQuery, placeholder: columns[0]?.header }}
            /* No "new activity" (2026-10-22): the activities are the system's fixed set of
               events, edited in place — only badges and risk levels are added here. */
            actions={
              tab !== 'activities' && (
                <Button onClick={() => open(null)}>
                  <Plus size={16} strokeWidth={2} />
                  {formLabels.add}
                </Button>
              )
            }
          />

          <DataTable
            columns={sortableColumns}
            rows={rows}
            loading={listLoading}
            query={query}
            getRowId={(row) => row.id}
            emptyMessage={formLabels.empty}
            server={{
              page,
              pageSize,
              total,
              sort,
              onChange: (next) => {
                setPage(next.page)
                setPageSize(next.pageSize)
                setSort(next.sort)
              },
            }}
          />
        </>
      )}

      <Drawer
        dialog
        open={tab !== 'settings' && !!editing}
        title={editing?.id ? formLabels.edit : formLabels.add}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={submit} disabled={saving}>
              {S.save}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {S.close}
            </Button>
          </>
        }
      >
        {/* The kit's form spacing: the drawer's own 8px gap let each field's floating
            label sit on the box above it. */}
        <FormBody>
          {tab === 'activities' && (
            <>
              <FormRow label={S.activityForm.eventType}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.eventType}
                    onChange={set('eventType')}
                    options={settings?.eventTypes ?? []}
                  />
                )}
              </FormRow>
              {Number(values.eventType) === EXAM_EVENT_TYPE ? (
                <InfoNote icon={Info}>
                  <bdi>{S.activityForm.examNote}</bdi>
                </InfoNote>
              ) : (
                <FormRow label={S.activityForm.points}>
                  {(id) => (
                    <TextField id={id} dir="ltr" inputMode="numeric" value={String(values.points ?? 0)} onChange={set('points')} />
                  )}
                </FormRow>
              )}
              <FormRow label={S.activityForm.percentage} hint={S.activityForm.percentageHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    inputMode="numeric"
                    value={values.percentageOnDuplication ?? ''}
                    onChange={set('percentageOnDuplication')}
                  />
                )}
              </FormRow>
              <SwitchField
                checked={values.isPositive}
                onChange={set('isPositive')}
                label={S.activityForm.isPositive}
                hint={S.activityForm.isPositiveHint}
              />
              <SwitchField
                checked={values.effectOpposite}
                onChange={set('effectOpposite')}
                label={S.activityForm.effectOpposite}
              />
            </>
          )}

          {/* The two languages of one value side by side, read as the pair they are; the
              points take one column, so the number does not stretch across the sheet. */}
          {tab === 'badges' && (
            <>
              <FormCols>
                <FormRow label={S.nameAr}>
                  {(id) => <TextField id={id} value={values.nameAr} onChange={set('nameAr')} />}
                </FormRow>
                <FormRow label={S.nameEn}>
                  {(id) => <TextField id={id} dir="ltr" value={values.nameEn} onChange={set('nameEn')} />}
                </FormRow>
              </FormCols>
              <FormCols>
                <FormRow label={S.badgeForm.points}>
                  {(id) => (
                    <TextField id={id} dir="ltr" inputMode="numeric" value={String(values.points ?? 0)} onChange={set('points')} />
                  )}
                </FormRow>
              </FormCols>
              <FormCols>
                <FormRow label={S.descriptionAr}>
                  {(id) => <Textarea id={id} rows={2} value={values.descriptionAr} onChange={set('descriptionAr')} />}
                </FormRow>
                <FormRow label={S.descriptionEn}>
                  {(id) => (
                    <Textarea id={id} rows={2} dir="ltr" value={values.descriptionEn} onChange={set('descriptionEn')} />
                  )}
                </FormRow>
              </FormCols>
              <FormRow label={S.badgeForm.image} hint={S.badgeForm.imageHint}>
                {(id) => <FileField id={id} accept=".png" onChange={readImage} />}
              </FormRow>
            </>
          )}

          {tab === 'riskLevels' && (
            <>
              <FormCols>
                <FormRow label={S.nameAr}>
                  {(id) => <TextField id={id} value={values.nameAr} onChange={set('nameAr')} />}
                </FormRow>
                <FormRow label={S.nameEn}>
                  {(id) => <TextField id={id} dir="ltr" value={values.nameEn} onChange={set('nameEn')} />}
                </FormRow>
              </FormCols>
              <FormCols>
                <FormRow label={S.riskForm.min}>
                  {(id) => (
                    <TextField id={id} dir="ltr" inputMode="numeric" value={String(values.min ?? 0)} onChange={set('min')} />
                  )}
                </FormRow>
                <FormRow label={S.riskForm.max} hint={S.riskForm.maxHint}>
                  {(id) => <TextField id={id} dir="ltr" inputMode="numeric" value={values.max ?? ''} onChange={set('max')} />}
                </FormRow>
              </FormCols>
            </>
          )}

          <SwitchField
            checked={values.isActive !== false}
            onChange={set('isActive')}
            label={formLabels.enabled}
          />
        </FormBody>
      </Drawer>
    </>
  )
}
